import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, IconButton,
  CircularProgress,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment';
import {
  Form, useTranslate, required,
  Confirm,
} from 'react-admin';
import { useEffect, useState } from 'react';
import { type ExpiryTimeGranularity, ExpiryTimeGranularityType } from '@x-guard/xgac-types/xgac';
import { useFormState, useWatch } from 'react-hook-form';
import { ExpiryDateDisplay } from './ExpiryDateDisplay';
import { CertificationDocumentInput } from './inputs/CertificationDocumentInput';
import { CertificationUserTable } from './CertificationUserTable';
import { type CertificationDocument, type DocumentAssignee, hasCertificationDocument } from '../../../utils/certificationDocuments';
import {
  buildDefaultCertificationDetails,
  type CertificationsByAssetId,
} from '../../../utils/certificationUserForm';
import { notLaterThanToday } from '../../../utils/dateValidators';

type RenewNowStartDateKey = 'lastInspectionDate' | 'lastReceivedOn';

type RenewNowSingleFormValuesBase = {
  expiryDate: string;
  document?: CertificationDocument;
};

export type RenewNowFormValues =
  | ({ lastInspectionDate: string } & RenewNowSingleFormValuesBase)
  | ({ lastReceivedOn: string } & RenewNowSingleFormValuesBase)
  | { certificationsByAssetId: CertificationsByAssetId };

const RenewNowDialogActions = (props: {
  onBack?: () => void;
  onClose: () => void;
  requiresDocumentaryProof?: boolean;
  isBulk: boolean;
  recipients: DocumentAssignee[];
}) => {

  const translate = useTranslate();
  const { isSubmitting } = useFormState();

  const certificationsByAssetId: CertificationsByAssetId | undefined = useWatch({
    name: 'certificationsByAssetId',
  });

  const document: CertificationDocument | undefined = useWatch({ name: 'document' });

  const documentsReady = !props.requiresDocumentaryProof || (
    props.isBulk
      ? props.recipients.every((r) => hasCertificationDocument(certificationsByAssetId?.[r.id]?.document))
      : hasCertificationDocument(document));

  return (
    <DialogActions>
      <Button onClick={props.onClose} disabled={isSubmitting}>
        {translate('ra.action.cancel')}
      </Button>
      <Button
        type="submit"
        form="renew-now-form"
        variant="contained"
        disabled={isSubmitting || !documentsReady}
        startIcon={
          isSubmitting
            ? <CircularProgress color="inherit" size={20} />
            : undefined
        }
      >
        {translate('ra.action.confirm')}
      </Button>
    </DialogActions>
  );

};

const RenewNowDialogTitle = (props: {
  title: string;
  onBack?: () => void;
  onClose: () => void;
}) => {

  const translate = useTranslate();
  const { isSubmitting } = useFormState();

  return (
    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {props.onBack && (
          <IconButton onClick={props.onBack} aria-label={translate('ra.action.back')} disabled={isSubmitting}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant="body1" fontSize="20px">
          {translate(props.title)}
        </Typography>
      </Stack>
      <IconButton
        onClick={props.onClose}
        aria-label={translate('general.text.close')}
        disabled={isSubmitting}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
  );

};

export const RenewNowModal = (props: {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  onSubmit: (values: RenewNowFormValues) => Promise<void>;
  timeGranularity?: ExpiryTimeGranularity;
  startDateSource?: RenewNowStartDateKey;
  expiryDateSource?: string;
  startDateLabel: string;
  expiryDateLabel: string;
  helperText: string;
  title: string;
  hasDocumentaryProofField?: boolean;
  bulkRecipients?: DocumentAssignee[];
  formSessionKey?: string | number;
}) => {

  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const translate = useTranslate();
  const startDateSource = props.startDateSource ?? 'lastInspectionDate';
  const expiryDateSource = props.expiryDateSource ?? 'expiryDate';

  const isBulkCertificationFlow = props.bulkRecipients !== undefined
    && startDateSource === 'lastReceivedOn';

  const today = moment().format('YYYY-MM-DD');
  const expiry = props.timeGranularity
    ? moment().add(props.timeGranularity.value, props.timeGranularity.type).format('YYYY-MM-DD')
    : today;

  useEffect(() => {

    if (!props.open) {

      setDiscardConfirmOpen(false);

    }

  }, [props.open]);

  const requestClose = () => setDiscardConfirmOpen(true);

  const confirmDiscard = () => {

    setDiscardConfirmOpen(false);
    props.onClose();

  };

  const defaultValues = isBulkCertificationFlow
    ? {
      certificationsByAssetId: buildDefaultCertificationDetails(
        props.bulkRecipients?.map(({ id }) => id) ?? [],
        today,
        expiry,
        false,
      ),
    }
    : {
      [startDateSource]: today,
      [expiryDateSource]: expiry,
    };

  const handleSubmit = async (values: Record<string, unknown>) => {

    if (isBulkCertificationFlow) {

      await props.onSubmit({
        certificationsByAssetId: values.certificationsByAssetId as CertificationsByAssetId,
      });
      return;

    }

    const formValues: RenewNowFormValues = startDateSource === 'lastReceivedOn'
      ? {
        lastReceivedOn: values[startDateSource] as string,
        expiryDate: values[expiryDateSource] as string,
        document: values.document as CertificationDocument | undefined,
      }
      : {
        lastInspectionDate: values[startDateSource] as string,
        expiryDate: values[expiryDateSource] as string,
        document: values.document as CertificationDocument | undefined,
      };

    await props.onSubmit(formValues);

  };

  return (
    <>
      <Dialog
        open={props.open}
        keepMounted={Boolean(props.onBack)}
        maxWidth={isBulkCertificationFlow ? 'lg' : 'md'}
        fullWidth
        sx={{
          '& .MuiDialog-paper': { overflowY: 'hidden' },
          '& form': {
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
          },
        }}
      >
        <Form
          id="renew-now-form"
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          key={props.formSessionKey ?? 'renew-now'}
        >
          <RenewNowDialogTitle
            title={props.title}
            onBack={props.onBack}
            onClose={requestClose}
          />
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 3 }}>
              {isBulkCertificationFlow ? (
                <CertificationUserTable
                  recipients={props.bulkRecipients ?? []}
                  timeGranularity={props.timeGranularity ?? { value: 1, type: ExpiryTimeGranularityType.Year }}
                  includeFirstIssuedOn={false}
                  requiresDocument={Boolean(props.hasDocumentaryProofField)}
                />
              ) : (
                <>
                  <ExpiryDateDisplay
                    startDateSource={startDateSource}
                    expiryDateSource={expiryDateSource}
                    timeGranularity={props.timeGranularity}
                    startDateLabel={props.startDateLabel}
                    expiryDateLabel={props.expiryDateLabel}
                    helperText={props.helperText}
                    usage="certifications"
                    startDateValidate={
                      startDateSource === 'lastReceivedOn'
                        ? notLaterThanToday(
                          'resources.assets.certifications.validation.last_received_in_future',
                        )
                        : undefined
                    }
                  />
                  {props.hasDocumentaryProofField && (
                    <CertificationDocumentInput
                      source="document"
                      label={false}
                      validate={required()}
                      labelSingle={translate(
                        'resources.assets.certifications.text.drop_document_to_upload',
                      )}
                      accept={{
                        'application/pdf': ['.pdf'],
                        'image/*': ['.jpg', '.jpeg', '.png'],
                      }}
                    />
                  )}
                </>
              )}
            </Stack>
          </DialogContent>
          <RenewNowDialogActions
            onBack={props.onBack}
            onClose={requestClose}
            requiresDocumentaryProof={Boolean(props.hasDocumentaryProofField)} isBulk={isBulkCertificationFlow}
            recipients={props.bulkRecipients ?? []} />
        </Form>
      </Dialog>
      <Confirm
        isOpen={discardConfirmOpen}
        title="resources.certification-categories.text.discard_changes_title"
        content="resources.certification-categories.text.discard_changes_content"
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={confirmDiscard}
      />
    </>
  );

};
